import { Box, Button, Link } from "@mui/material";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecordContext, useTheme, useCreatePath, useResourceContext } from "react-admin";
import { ZohoBaseUrl, ZohoModulesUrl } from "../../enums/zoho";
import { UserType } from "../../enums/common";
import CustomCallIconButton from "../Common/CallButton/CustomCallIconButton";
import WalletDetails from "../Common/WalletDetails";
import { formatDateTime } from "../../utils/DateTimeUtils";
import {
  FlexBetween,
  HorizontalContainer,
  HorizontalLine,
  InfoPill,
  LeftWorkspacePanel,
  MapButton,
  MapHeaderContainer,
  Paragraph,
  RightWorkspacePanel,
  SectionHeader,
  SectionTitle,
  ShowLabel,
  ShowValue,
  StatusBooleanBadge,
  ThreeColGrid,
  WorkspacePanel,
  WorkspacePanelsWrapper,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import {
  EditIcon,
  LocationIcon,
  UserIcon,
  PhoneIcon,
  ClipboardIcon,
  RecieptIcon,
} from "../Svg";
import { colors } from "../../styles/colors";

const mapContainerStyle = {
  width: "100%",
  height: "50vh",
};

const CustomerMetaShow = (props: any) => {
  const record = useRecordContext();
  const { userWallet } = props;
  const [showMap, setShowMap] = useState(false);
  const navigate = useNavigate();
  const createPath = useCreatePath();

  const resource = useResourceContext();

  const handleEditClick = () => {
    if (!record) return;
    const editPath = createPath({ type: 'edit', resource, id: record.id });
    navigate(editPath, { state: { from: 'show' } });
  };

  const [theme] = useTheme();
  const currentTheme = (theme as any).palette?.mode;

  const coords = record?.lastKnownLocation?.coordinates;
  // GeoJSON format: [longitude, latitude]
  const lng: number | null = coords?.[0] ?? null;
  const lat: number | null = coords?.[1] ?? null;
  const hasLocation = lat !== null && lng !== null;

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string,
  });

  const onUnmount = useCallback(() => {}, []);

  return (
    <div>
      {/* ── Top action bar ── */}
      <FlexBetween style={{ marginBottom: "16px", flexWrap: "wrap", gap: "8px" }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              backgroundColor: "rgba(25, 117, 210, 0.1)",
              padding: "8px 14px",
              borderRadius: "100px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <UserIcon color={colors.primary} width={16} height={16} />
            <ShowLabel style={{ margin: 0, color: colors.primary, fontWeight: 600 }}>
              Customer #{record?.id}
            </ShowLabel>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <WalletDetails userWallet={userWallet} />
          <Button
            size="small"
            startIcon={<EditIcon color="inherit" />}
            onClick={handleEditClick}
            sx={{
              textTransform: "none",
              border: "1px solid rgba(25, 117, 210, 0.9)",
              backgroundColor: currentTheme === "dark" ? "rgba(25,117,210,0.08)" : "#fff",
              color: "rgba(25, 117, 210, 0.9)",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "14px",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(25, 117, 210, 0.9)",
                color: "#fff",
                boxShadow: "none",
              },
            }}
          >
            Edit
          </Button>
        </Box>
      </FlexBetween>

      <WorkspacePanelsWrapper>
        {/* ══ LEFT PANEL ══ */}
        <LeftWorkspacePanel>

          {/* ── Profile Overview Card ── */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <UserIcon color={colors.primary} height={20} width={20} />
                  <SectionTitle>Profile Overview</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <ThreeColGrid>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>First Name:</ShowLabel>
                <ShowValue>{record?.fname || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Last Name:</ShowLabel>
                <ShowValue>{record?.lname || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Middle Name:</ShowLabel>
                <ShowValue>{record?.mname || "-"}</ShowValue>
              </HorizontalContainer>
            </ThreeColGrid>

            <HorizontalLine />

            <ThreeColGrid>
              <HorizontalContainer>
                <PhoneIcon />
                <ShowLabel style={{ marginBottom: "0px" }}>Mobile:</ShowLabel>
                <CustomCallIconButton
                  color="#6C7C93"
                  size={16}
                  toPhoneNumber={record?.mobile}
                  userType={UserType.COOK}
                  referenceId={Number(record?.id)}
                  referenceModelName="CUSTOMER_MODEL"
                />
                <ShowValue>{record?.mobile || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Email:</ShowLabel>
                <ShowValue>{record?.email || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Tag:</ShowLabel>
                <ShowValue>{record?.tag || "-"}</ShowValue>
              </HorizontalContainer>
            </ThreeColGrid>

            <HorizontalLine />

            <ThreeColGrid>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Gender:</ShowLabel>
                <ShowValue>{record?.gender || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Food Preference:</ShowLabel>
                <ShowValue>{record?.foodPreference || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Rating By Cooks:</ShowLabel>
                <ShowValue>
                  {record?.ratingByCooks != null ? `${record.ratingByCooks}/5` : "-"}
                </ShowValue>
              </HorizontalContainer>
            </ThreeColGrid>
          </WorkspacePanel>

          <br />

          {/* ── Account Details Card ── */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <ClipboardIcon color={colors.primary} height={20} width={20} />
                  <SectionTitle>Account Details</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <ThreeColGrid>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>#</ShowLabel>
                <ShowLabel style={{ marginBottom: "0px" }}>Customer ID:</ShowLabel>
                <ShowValue>{record?.id ?? "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Account ID:</ShowLabel>
                <ShowValue>{record?.accountId ?? "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Account Status:</ShowLabel>
                <ShowValue>{record?.accountStatus ?? "-"}</ShowValue>
              </HorizontalContainer>
            </ThreeColGrid>

            <HorizontalLine />

            <ThreeColGrid>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Contact ID:</ShowLabel>
                <ShowValue>{record?.contactId || "-"}</ShowValue>
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Zoho ID:</ShowLabel>
                {record?.zohoId ? (
                  <Link
                    href={`${ZohoBaseUrl}${ZohoModulesUrl.CUSTOMER}/${record.zohoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ fontSize: "14px", color: colors.primary }}
                  >
                    {record.zohoId}
                  </Link>
                ) : (
                  <ShowValue>-</ShowValue>
                )}
              </HorizontalContainer>
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Last Activity:</ShowLabel>
                <ShowValue>
                  {record?.Last_Activity_Time
                    ? formatDateTime(record.Last_Activity_Time)
                    : "-"}
                </ShowValue>
              </HorizontalContainer>
            </ThreeColGrid>

            <HorizontalLine />

            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Address:</ShowLabel>
              <ShowValue>{record?.address || "-"}</ShowValue>
            </HorizontalContainer>
          </WorkspacePanel>

        </LeftWorkspacePanel>

        {/* ══ RIGHT PANEL ══ */}
        <RightWorkspacePanel>

          {/* ── Additional Details Card ── */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <RecieptIcon color={colors.primary} height={20} width={20} />
                  <SectionTitle>Additional Details</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Summary Combo SKU ID:</ShowLabel>
              <ShowValue>{record?.summaryPageComboSkuId ?? "-"}</ShowValue>
            </HorizontalContainer>

            <HorizontalLine />

            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Discount Coupon ID:</ShowLabel>
              <ShowValue>{record?.discountCouponId ?? "-"}</ShowValue>
            </HorizontalContainer>

            <HorizontalLine />

            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Combo Page SKU List:</ShowLabel>
              <ShowValue>
                {record?.comboPageSkuIdList
                  ? Array.isArray(record.comboPageSkuIdList)
                    ? record.comboPageSkuIdList.join(", ")
                    : String(record.comboPageSkuIdList)
                  : "-"}
              </ShowValue>
            </HorizontalContainer>

            <HorizontalLine />

            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Block Promo SMS:</ShowLabel>
              <StatusBooleanBadge active={record?.blockPromotionalSMS}>
                {record?.blockPromotionalSMS ? "Blocked" : "Allowed"}
              </StatusBooleanBadge>
            </HorizontalContainer>
          </WorkspacePanel>

          <br />

          {/* ── Location Card ── */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <LocationIcon color={colors.primary} height={20} width={20} />
                  <SectionTitle>Last Known Location</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px", whiteSpace: "nowrap" }}>Geo City:</ShowLabel>
              <ShowValue style={{ whiteSpace: "nowrap" }}>{record?.geoCity || "-"}</ShowValue>
              <ShowLabel style={{ marginBottom: "0px", marginLeft: "16px", whiteSpace: "nowrap" }}>Geo Area:</ShowLabel>
              <ShowValue style={{ whiteSpace: "nowrap" }}>{record?.geoArea || "-"}</ShowValue>
            </HorizontalContainer>

            {hasLocation && (
              <>
                <HorizontalLine />
                <ShowLabel style={{ marginBottom: "4px", fontWeight: 600 }}>Last Known Location</ShowLabel>
                <ShowValue style={{ fontSize: "12px", wordBreak: "break-all", marginBottom: "2px" }}>
                  Lat: {lat}
                </ShowValue>
                <ShowValue style={{ fontSize: "12px", wordBreak: "break-all" }}>
                  Lng: {lng}
                </ShowValue>
              </>
            )}

            {/* Info pills for city/area */}
            {(record?.geoCity || record?.geoArea) && (
              <>
                <HorizontalLine />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {record?.geoCity && (
                    <InfoPill>
                      <LocationIcon color={colors.primary} width={14} height={14} />
                      <ShowLabel style={{ marginBottom: "0", fontWeight: "normal", fontSize: "14px" }}>
                        {record.geoCity}
                      </ShowLabel>
                    </InfoPill>
                  )}
                  {record?.geoArea && (
                    <InfoPill style={{ backgroundColor: "rgba(25, 117, 210, 0.05)", border: `1px solid rgba(25, 117, 210, 0.15)` }}>
                      <LocationIcon color={colors.primaryDark || colors.primary} width={14} height={14} />
                      <ShowLabel style={{ marginBottom: "0", fontWeight: "normal", fontSize: "14px" }}>
                        {record.geoArea}
                      </ShowLabel>
                    </InfoPill>
                  )}
                </Box>
              </>
            )}
          </WorkspacePanel>

        </RightWorkspacePanel>
      </WorkspacePanelsWrapper>

      {/* ── Map — full width, same pattern as HouseMetaShow ── */}
      {hasLocation && (
        <Box
          sx={{
            marginTop: "20px",
            "& .MuiStack-root > :not(style) + :not(style)": { marginTop: 0 },
          }}
        >
          <MapHeaderContainer>
            <div>
              <WorkSpaceTitleRow>
                <LocationIcon />
                <Paragraph>Last Known Location</Paragraph>
              </WorkSpaceTitleRow>
            </div>
            <div>
              <MapButton onClick={() => setShowMap((v) => !v)}>
                {showMap ? "Hide Location" : "View on Map"}
              </MapButton>
            </div>
          </MapHeaderContainer>

          {isLoaded && showMap ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={{ lat: lat!, lng: lng! }}
              zoom={15}
              onUnmount={onUnmount}
            >
              <Marker position={{ lat: lat!, lng: lng! }} />
            </GoogleMap>
          ) : (
            <Box
              sx={{
                height: "300px",
                backgroundImage:
                  currentTheme === "dark"
                    ? "url(/map-placeholder-dark-bg.webp)"
                    : "url('/map-placeholder.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(1px)",
                borderRadius: "0px 0px 12px 12px",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.4)",
                }}
              >
                <Paragraph>Click 'View on Map' to load</Paragraph>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default CustomerMetaShow;
